if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      isAutoSelectingVariantOptions = false;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        let activeColor = null;
        this.preProcessHtmlCallbacks.push((html) => {
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
          
          const checkedInput = this.querySelector('input[name="meta_color_selection"]:checked');
          if (checkedInput) {
            activeColor = checkedInput.value;
          }
        });

        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();

          // Restore active color selection
          if (activeColor) {
            const inputToCheck = newNode.querySelector(`input[name="meta_color_selection"][value="${activeColor}"]`);
            if (inputToCheck) {
              inputToCheck.checked = true;
              
              // Update the hidden input in the form
              const hiddenInput = newNode.querySelector('#Selected-Meta-Color-Input');
              if (hiddenInput) {
                hiddenInput.value = activeColor;
              }
              
              // Update the label
              const selectedLabel = newNode.querySelector('#selected-meta-color-label');
              if (selectedLabel) {
                selectedLabel.innerText = activeColor;
              }
            }
          }

          // re-init slider after DOM update
          setTimeout(() => {
            initializeProductGallery();
            postLengthInit();
          }, 50);
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        if (target && target.name === 'meta_color_selection') return;

        this.resetProductFormState();

        const changedOptionIndex = this.getChangedOptionIndex(target);

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        const shouldAutoSelectNextOptions =
          changedOptionIndex === 1 && !this.isAutoSelectingVariantOptions;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, {
              shouldAutoSelectNextOptions,
            }),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl, options = {}) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          /* OPTION VALUE HANDLING */

          const updateOptionText = (selector, transform = (v) => v) => {
            const el = this.querySelector(selector);
            if (!el || !variant) return;

            const index = parseInt(el.dataset.optionIndex, 10);
            if (isNaN(index) || !variant.options?.[index]) return;

            el.textContent = transform(variant.options[index]);
          };

          updateOptionText('#prod-vari-metal-value');

          // updateOptionText('#prod-vari-caret-value', (value) => {
          //   return value.includes(' - ') ? value.split(' - ')[0] : value;
          // });

          updateOptionText('#prod-vari-caret-value', (value) => {
            const caretValue = value.includes(' - ')
              ? value.split(' - ')[0].trim()
              : value.trim();

            return /\d/.test(caretValue) ? caretValue : '';
          });

          updateOptionText('#prod-vari-caret-metal', (value) => {
            return value.includes(' - ') ? value.split(' - ').pop() : value;
          });

          /* GENERIC FIELD SYNC */

          const syncField = (id) => {
            const currentEls = this.querySelectorAll(`#${id}`);
            const updatedEls = html.querySelectorAll(`#${id}`);

            if (!currentEls.length) return;

            currentEls.forEach((currentEl, index) => {
              const updatedEl = updatedEls[index];

              if (updatedEl) {
                currentEl.textContent = updatedEl.textContent.trim();

                currentEl.closest('.accordion-item-content')?.classList.remove('hidden');
                currentEl.closest('.accordion-item')?.classList.remove('prod-have-no-value');
              } else {
                currentEl.textContent = '';

                currentEl.closest('.accordion-item-content')?.classList.add('hidden');
                currentEl.closest('.accordion-item')?.classList.add('prod-have-no-value');
              }
            });
          };

          /* APPLY SYNC */

          [
            'prod-vari-sku',
            'prod-vari-net-weight',
            'prod-vari-diamond-shape',
            'prod-vari-diamond-clarity',
            'prod-vari-no-of-diamond',
            'prod-vari-diamond-weight',
            'prod-vari-rate-per-gm',
            'prod-vari-weight',
            'prod-vari-gold-total',
            'prod-vari-diamond-count',
            'prod-vari-diamond-total',
            'prod-vari-additional-charges',
            'prod-vari-making-charges',
            'prod-vari-gst-percentage',
            'prod-vari-gst-amount',
            'prod-vari-grand-total'
          ].forEach(syncField);

          this.updateVariantDetailsAccordion(html);

          updateEmptyAccordionItems(this);
          updatePriceBreakupRateClass(this);
          initPriceBreakupTableObserver(this);
          updateDiamondTotalWrapperClass(this);
          updateGrandTotalWrapperClass(this);
          this.updateOptionValues(html);          

          if (options.shouldAutoSelectNextOptions && this.autoSelectFirstDownstreamOptions(1)) {
            return;
          }

          this.pickupAvailability?.update(variant);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateLiveGoldPriceBreakup(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          this.updateCustomVariantMediaBlocks(html);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantDetailsAccordion(html) {
        const updatedProductInfo =
          html.querySelector(`product-info[data-section="${this.dataset.section}"]`) || html;

        const currentDetailsAccordion = this.querySelector(
          '.product-accordion-main .variant-product-details'
        );

        const updatedDetailsAccordion = updatedProductInfo.querySelector(
          '.product-accordion-main .variant-product-details'
        );

        if (!currentDetailsAccordion && !updatedDetailsAccordion) return;

        if (currentDetailsAccordion && !updatedDetailsAccordion) {
          currentDetailsAccordion.style.display = 'none';
          currentDetailsAccordion.classList.add('hidden');
          return;
        }

        if (!currentDetailsAccordion && updatedDetailsAccordion) {
          const accordionMain = this.querySelector('.product-accordion-main');

          if (accordionMain) {
            accordionMain.prepend(updatedDetailsAccordion.cloneNode(true));
          }

          return;
        }

        const currentContent = currentDetailsAccordion.querySelector('.accordion-content');
        const updatedContent = updatedDetailsAccordion.querySelector('.accordion-content');

        const wasOpen =
          currentDetailsAccordion.classList.contains('is-open') ||
          currentContent?.classList.contains('is-open') ||
          currentContent?.style.maxHeight === 'none';

        if (currentContent && updatedContent) {
          currentContent.innerHTML = updatedContent.innerHTML;
        } else {
          currentDetailsAccordion.innerHTML = updatedDetailsAccordion.innerHTML;
        }

        currentDetailsAccordion.style.display = '';
        currentDetailsAccordion.classList.remove('hidden');

        if (wasOpen) {
          const newContent = currentDetailsAccordion.querySelector('.accordion-content');

          currentDetailsAccordion.classList.add('is-open');

          if (newContent) {
            newContent.classList.add('is-open');
            newContent.style.maxHeight = 'none';
          }
        }
      }

      updateLiveGoldPriceBreakup(variantId) {
        if (!variantId) return;

        const selectedVariantId = String(variantId);
        const priceBreakupWrapper = document.getElementById('product-price-breakup');

        if (
          typeof window.updatePriceBreakup === 'function' &&
          window.variantConfigs &&
          window.variantConfigs[selectedVariantId]
        ) {
          if (priceBreakupWrapper) {
            priceBreakupWrapper.style.display = 'block';
          }

          window.updatePriceBreakup(selectedVariantId);
        } else if (priceBreakupWrapper) {
          priceBreakupWrapper.style.display = 'none';
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }
      updateCustomVariantMediaBlocks(html) {
        const currentProductInfo = this;
        const updatedProductInfo = html.querySelector(`product-info[data-section="${this.dataset.section}"]`) || html;

        const selectors = [
          '.block_product-varient--images',
          '.block_product-varient-images-bottom',
        ];

        selectors.forEach((selector) => {
          const currentBlock = currentProductInfo.querySelector(selector);
          const updatedBlock = updatedProductInfo.querySelector(selector);

          if (!currentBlock) return;

          if (updatedBlock) {
            currentBlock.innerHTML = updatedBlock.innerHTML;
            currentBlock.className = updatedBlock.className;
          } else {
            currentBlock.innerHTML = '';
          }
        });

        requestAnimationFrame(() => {
          if (typeof window.swiperMainInstance?.destroy === 'function') {
            window.swiperMainInstance.destroy(true, true);
            window.swiperMainInstance = null;
          }

          if (typeof window.swiperThumbsInstance?.destroy === 'function') {
            window.swiperThumbsInstance.destroy(true, true);
            window.swiperThumbsInstance = null;
          }

          if (typeof initializeProductGallery === 'function') {
            initializeProductGallery();
          }

          if (typeof postLengthInit === 'function') {
            postLengthInit();
          }

          if (window.jQuery && typeof window.jQuery.fancybox === 'function') {
            window.jQuery('[data-fancybox]').fancybox();
          }
        });
      }

      getChangedOptionIndex(target) {
        const optionElement = target?.closest?.('[data-option-index]') || target;
        const optionIndex = optionElement?.dataset?.optionIndex;

        return optionIndex ? Number(optionIndex) : null;
      }

      autoSelectFirstDownstreamOptions(changedOptionIndex) {
        const variantSelectors = this.variantSelectors;
        if (!variantSelectors) return false;

        const downstreamOptionIndexes = [2, 3].filter((optionIndex) => optionIndex > changedOptionIndex);

        let hasSelectionChanged = false;
        let lastChangedControl = null;

        downstreamOptionIndexes.forEach((optionIndex) => {
          const optionGroup = variantSelectors.querySelector(`[data-option-index="${optionIndex}"]`);
          if (!optionGroup) return;

          const changedControl = this.selectFirstAvailableOption(optionGroup);
          if (changedControl) {
            hasSelectionChanged = true;
            lastChangedControl = changedControl;
          }
        });

        if (!hasSelectionChanged || !lastChangedControl) return false;

        this.isAutoSelectingVariantOptions = true;

        requestAnimationFrame(() => {
          lastChangedControl.dispatchEvent(new Event('change', { bubbles: true }));

          requestAnimationFrame(() => {
            this.isAutoSelectingVariantOptions = false;
          });
        });

        return true;
      }

      selectFirstAvailableOption(optionGroup) {
        const select = optionGroup.matches?.('select') ? optionGroup : optionGroup.querySelector('select');

        if (select) {
          const firstAvailableOption = Array.from(select.options).find((option) => {
            return option.value && !option.disabled && option.getAttribute('aria-disabled') !== 'true';
          });

          if (!firstAvailableOption || select.value === firstAvailableOption.value) return null;

          select.value = firstAvailableOption.value;
          return select;
        }

        const firstAvailableInput = Array.from(optionGroup.querySelectorAll('input[type="radio"]')).find((input) => {
          return (
            !input.disabled &&
            input.getAttribute('aria-disabled') !== 'true' &&
            !input.closest('.disabled') &&
            !input.closest('[disabled]')
          );
        });

        if (!firstAvailableInput || firstAvailableInput.checked) return null;

        firstAvailableInput.checked = true;
        return firstAvailableInput;
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
function updateEmptyAccordionItems(root = document) {
  root.querySelectorAll('.accordion-item').forEach((item) => {
    const contentEl = item.querySelector('.accordion-item-content');

    if (!contentEl) return;

    const spans = contentEl.querySelectorAll('span');

    let value = '';

    if (spans.length) {
      value = Array.from(spans)
        .map((span) => span.textContent.trim())
        .join('');
    } else {
      value = contentEl.textContent.trim();
    }

    item.classList.toggle('prod-have-no-value', value === '');
  });
}

// Rate per gm JS
// function updatePriceBreakupRateClass(root = document) {
//   const priceBreakupWrapper = root.querySelector('#product-price-breakup');

//   if (!priceBreakupWrapper) return;

//   // const rateEl = root.querySelector('#prod-vari-rate-per-gm');
//   // const hasRate = rateEl && rateEl.textContent.trim() !== '';

//   // priceBreakupWrapper.classList.toggle('produ-not-have-rate', !hasRate);
// }

function updatePriceBreakupRateClass(root = document) {
  const priceBreakupWrapper =
    root.querySelector?.('.produ-price-breakup') ||
    document.querySelector('.produ-price-breakup');

  const priceBreakupTable =
    root.querySelector?.('#price-breakup-table') ||
    document.getElementById('price-breakup-table');

  if (!priceBreakupWrapper || !priceBreakupTable) return;

  const tableText = priceBreakupTable.textContent.replace(/\s+/g, '').trim();

  const hasFilledRows = Array.from(priceBreakupTable.querySelectorAll('tr')).some((row) => {
    return row.textContent.replace(/\s+/g, '').trim() !== '';
  });

  const hasTableData = tableText !== '' || hasFilledRows;

  priceBreakupWrapper.classList.toggle('produ-not-have-rate', !hasTableData);
}

const priceBreakupObserverMap = new WeakMap();

function initPriceBreakupTableObserver(root = document) {
  const priceBreakupTable =
    root.querySelector?.('#price-breakup-table') ||
    document.getElementById('price-breakup-table');

  if (!priceBreakupTable || priceBreakupObserverMap.has(priceBreakupTable)) return;

  const observer = new MutationObserver(() => {
    updatePriceBreakupRateClass(root);
  });

  observer.observe(priceBreakupTable, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  priceBreakupObserverMap.set(priceBreakupTable, observer);
}

// Diamond total wrapper JS
function updateDiamondTotalWrapperClass(root = document) {
  root.querySelectorAll('.prod-vari-diamond-total-wrap').forEach((wrapper) => {
    const diamondTotalEl = wrapper.querySelector('#prod-vari-diamond-total');

    const diamondTotalValue = diamondTotalEl
      ? diamondTotalEl.textContent.trim()
      : '';

    const numericValue = Number(
      diamondTotalValue
        .replace('₹', '')
        .replace(/,/g, '')
        .trim()
    );

    const hasNoDiamondTotal =
      diamondTotalValue === '' ||
      Number.isNaN(numericValue) ||
      numericValue === 0;

    wrapper.classList.toggle(
      'produ-not-have-vari-diam',
      hasNoDiamondTotal
    );
  });
}

// prod vari grand total
function updateGrandTotalWrapperClass(root = document) {
  root.querySelectorAll('.price-total').forEach((wrapper) => {
    const grandTotalEl = wrapper.querySelector('#prod-vari-grand-total');

    const grandTotalValue = grandTotalEl
      ? grandTotalEl.textContent.trim()
      : '';

    const numericValue = Number(
      grandTotalValue
        .replace('₹', '')
        .replace(/,/g, '')
        .trim()
    );

    const hasNoGrandTotal =
      grandTotalValue === '' ||
      Number.isNaN(numericValue) ||
      numericValue === 0;

    wrapper.classList.toggle(
      'prod-not-have-vari-grand-total',
      hasNoGrandTotal
    );
  });
}

// document.addEventListener("DOMContentLoaded", () => {
//   initializeProductGallery();
//   updateEmptyAccordionItems();
//   updatePriceBreakupRateClass();
//   updateDiamondTotalWrapperClass();
//   updateGrandTotalWrapperClass();
// });

document.addEventListener("DOMContentLoaded", () => {
  initializeProductGallery();
  updateEmptyAccordionItems();
  updatePriceBreakupRateClass();
  updateDiamondTotalWrapperClass();
  updateGrandTotalWrapperClass();

  initPriceBreakupTableObserver();

  setTimeout(() => {
    updatePriceBreakupRateClass();
    initPriceBreakupTableObserver();
  }, 100);

  setTimeout(() => {
    updatePriceBreakupRateClass();
    initPriceBreakupTableObserver();
  }, 500);

  setTimeout(() => {
    updatePriceBreakupRateClass();
    initPriceBreakupTableObserver();
  }, 1000);
});